/**
 * useTTS — Text-to-Speech hook using OpenAI TTS via backend proxy.
 *
 * Sends text to the backend `/api/tts` endpoint which calls OpenAI's TTS API,
 * then plays the returned audio. Supports mute/unmute and stop.
 */

import { useCallback, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';

interface UseTTSOptions {
  /** ISO language code, e.g. "de-DE" or "en-US" */
  lang?: string;
}

interface UseTTSReturn {
  /** Read text aloud via OpenAI TTS */
  speak: (text: string) => void;
  /** Cancel current speech */
  stop: () => void;
  /** Whether audio is currently playing */
  isSpeaking: boolean;
  /** Whether TTS output is muted */
  isMuted: boolean;
  /** Toggle mute on/off */
  toggleMute: () => void;
  /** Always true — OpenAI TTS is server-side */
  isSupported: boolean;
}

export function useTTS(options: UseTTSOptions = {}): UseTTSReturn {
  const { lang = 'de-DE' } = options;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  const speak = useCallback(
    (text: string) => {
      if (isMuted || !text.trim()) return;

      // Stop any ongoing playback
      stop();

      const controller = new AbortController();
      abortRef.current = controller;

      // Extract base language code (de-DE → de)
      const language = lang.split('-')[0];

      setIsSpeaking(true);

      fetch(`${API_URL}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, language }),
        signal: controller.signal,
      })
        .then((res) => {
          if (!res.ok) throw new Error(`TTS request failed (${res.status})`);
          return res.blob();
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audioRef.current = audio;

          audio.onended = () => {
            setIsSpeaking(false);
            audioRef.current = null;
            URL.revokeObjectURL(url);
          };

          audio.onerror = () => {
            setIsSpeaking(false);
            audioRef.current = null;
            URL.revokeObjectURL(url);
          };

          return audio.play();
        })
        .catch((err) => {
          if (err instanceof Error && err.name === 'AbortError') return;
          console.error('TTS playback failed:', err);
          setIsSpeaking(false);
        });
    },
    [isMuted, lang, stop]
  );

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      if (!prev) {
        // Muting: stop current playback
        stop();
      }
      return !prev;
    });
  }, [stop]);

  return {
    speak,
    stop,
    isSpeaking,
    isMuted,
    toggleMute,
    isSupported: true,
  };
}
